import { useEffect, useState } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Alert, Snackbar, Table, TableHead, TableRow, TableCell,
  TableBody, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { api } from '../../api/client.js';
import type { IDisclaimer } from '../../../shared/types.js';
import { colors } from '../../styles/theme.js';

function previewText(text: string, maxLen = 140): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= maxLen) return t || '—';
  return `${t.slice(0, maxLen)}…`;
}

function formatDt(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export default function DisclaimerPage() {
  const [disclaimers, setDisclaimers] = useState<IDisclaimer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editId, setEditId] = useState<number | null>(null);
  const [formContent, setFormContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);
  /** Erster Disclaimer direkt auf der Seite (ohne Tabelle) */
  const [emptyEditorContent, setEmptyEditorContent] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError(null);
    const res = await api.get<{ disclaimers: IDisclaimer[] }>('/config/disclaimers');
    if (res.success) setDisclaimers(res.data.disclaimers);
    else setError(res.error.message);
    setLoading(false);
  };

  useEffect(() => {
    void loadData();
  }, []);

  const openCreate = () => {
    setError(null);
    setDialogMode('create');
    setEditId(null);
    setFormContent('');
    setDialogOpen(true);
  };

  const openEdit = (d: IDisclaimer) => {
    setError(null);
    setDialogMode('edit');
    setEditId(d.id);
    setFormContent(d.content);
    setDialogOpen(true);
  };

  const handleDialogSave = async () => {
    setSaving(true);
    setError(null);
    if (dialogMode === 'create') {
      const res = await api.post<{ disclaimer: IDisclaimer }>('/config/disclaimers', { content: formContent });
      if (res.success) {
        setDialogOpen(false);
        setSnackbar('Disclaimer angelegt.');
        void loadData();
      } else {
        setError(res.error.message);
      }
    } else if (editId != null) {
      const res = await api.patch<{ disclaimer: IDisclaimer }>(`/config/disclaimers/${editId}`, {
        content: formContent,
      });
      if (res.success) {
        setDialogOpen(false);
        setSnackbar('Disclaimer gespeichert.');
        void loadData();
      } else {
        setError(res.error.message);
      }
    }
    setSaving(false);
  };

  const handleFirstSave = async () => {
    setSaving(true);
    setError(null);
    const res = await api.post<{ disclaimer: IDisclaimer }>('/config/disclaimers', { content: emptyEditorContent });
    if (res.success) {
      setEmptyEditorContent('');
      setSnackbar('Disclaimer gespeichert.');
      void loadData();
    } else {
      setError(res.error.message);
    }
    setSaving(false);
  };

  const latestId = disclaimers.length ? disclaimers[0].id : null;
  const showTable = !loading && disclaimers.length > 0;
  const showEmptyEditor = !loading && disclaimers.length === 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5">Disclaimer</Typography>
        {showTable && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Neuer Disclaimer
          </Button>
        )}
      </Box>

      {showTable && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Auf der Score Card erscheint der Eintrag mit dem neuesten Änderungsdatum.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Alle gespeicherten Disclaimer in der Tabelle; Zeile bearbeiten oder einen weiteren anlegen.
          </Typography>
        </>
      )}

      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, position: 'relative' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} />
          </Box>
        )}
        {showEmptyEditor && (
          <Box sx={{ p: 3 }}>
            <TextField
              fullWidth
              multiline
              minRows={10}
              value={emptyEditorContent}
              onChange={(e) => setEmptyEditorContent(e.target.value)}
              placeholder="Disclaimer-Text…"
              disabled={saving}
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { bgcolor: colors.white } }}
            />
            <Button variant="contained" onClick={() => void handleFirstSave()} disabled={saving}>
              {saving ? 'Speichern…' : 'Speichern'}
            </Button>
          </Box>
        )}
        {showTable && (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: colors.lightGrey }}>
                <TableCell sx={{ minWidth: 200 }}>Vorschau</TableCell>
                <TableCell width={200}>Stand</TableCell>
                <TableCell width={120}>Score Card</TableCell>
                <TableCell width={72} />
              </TableRow>
            </TableHead>
            <TableBody>
              {disclaimers.map((d) => (
                <TableRow key={d.id}>
                  <TableCell sx={{ verticalAlign: 'top' }}>{previewText(d.content)}</TableCell>
                  <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                    {formatDt(d.updated_at)}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    {d.id === latestId ? (
                      <Chip label="Aktiv" size="small" sx={{ bgcolor: colors.lime, color: colors.black, fontWeight: 600 }} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <IconButton size="small" onClick={() => openEdit(d)} aria-label="Bearbeiten">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{dialogMode === 'create' ? 'Neuer Disclaimer' : 'Disclaimer bearbeiten'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {error && dialogOpen && (
            <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
          )}
          <TextField
            label="Text"
            fullWidth
            multiline
            minRows={10}
            value={formContent}
            onChange={(e) => setFormContent(e.target.value)}
            placeholder="Disclaimer-Text…"
            disabled={saving}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: colors.white } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Abbrechen</Button>
          <Button variant="contained" onClick={() => void handleDialogSave()} disabled={saving}>
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ''}
      />
    </Box>
  );
}
